// +----------------------------------------------------------------------
// | 我的记忆 /memories：AI 眼里关于我的那些事
// | 隐私底线 —— 看得见、改得动、删得掉，还能一键清空
// +----------------------------------------------------------------------
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { addMemory, clearMemories, listMemories, removeMemory, updateMemory } from '../../api/memory';
import { getSettings, updateSettings } from '../../api/user';
import { toast } from '../../components/Toast';
import { formatFull } from '../../lib/format';
import './Memories.less';

// 与后端 Memory::TYPES 保持一致
const TYPE_META = [
  { value: '', label: '全部' },
  { value: 'preference', label: '偏好' },
  { value: 'habit', label: '习惯' },
  { value: 'decision_pattern', label: '决策模式' },
  { value: 'interest', label: '兴趣' },
  { value: 'profile', label: '画像' },
  { value: 'goal', label: '目标' },
  { value: 'fact', label: '事实' },
];

const SOURCE_LABEL = {
  decision: '来自你的选择',
  feedback: '来自复盘反馈',
  profile: '来自你的资料',
  manual: '你手动写的',
};

export default function Memories() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ list: [], total: 0, enabled: 1, max: 200, types: {} });
  const [activeType, setActiveType] = useState('');
  const [saving, setSaving] = useState(false);

  // 编辑态：一次只改一条
  const [editingId, setEditingId] = useState(0);
  const [editingText, setEditingText] = useState('');

  // 手动添加
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const [newType, setNewType] = useState('preference');

  const load = useCallback(async (type = activeType) => {
    setLoading(true);
    try {
      const res = await listMemories({ type: type || undefined, page: 1, page_size: 50 });
      setData({
        list: res.list || [],
        total: res.total || 0,
        enabled: res.enabled === 0 ? 0 : 1,
        max: res.max || 200,
        types: res.types || {},
      });
    } catch (e) {
      toast(e.message || '记忆没拉到', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeType]);

  useEffect(() => {
    load(activeType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType]);

  const totalCount = useMemo(
    () => Object.values(data.types || {}).reduce((sum, n) => sum + Number(n || 0), 0),
    [data.types]
  );

  const handleToggleMemory = async () => {
    const next = data.enabled === 1 ? 0 : 1;
    setSaving(true);
    try {
      await updateSettings({ memory_enabled: next });
      setData((prev) => ({ ...prev, enabled: next }));
      toast(next === 1 ? '好的，我记着' : '先不记了', 'success');
    } catch (e) {
      toast(e.message || '没改成功', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (id) => {
    const content = editingText.trim();
    if (!content) {
      toast('总得写点什么');
      return;
    }
    setSaving(true);
    try {
      const updated = await updateMemory(id, { content });
      setData((prev) => ({
        ...prev,
        list: prev.list.map((item) => (item.id === id ? { ...item, ...updated } : item)),
      }));
      setEditingId(0);
      setEditingText('');
      toast('改好了', 'success');
    } catch (e) {
      toast(e.message || '没保存上', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setSaving(true);
    try {
      await removeMemory(id);
      setData((prev) => ({ ...prev, list: prev.list.filter((item) => item.id !== id), total: Math.max(0, prev.total - 1) }));
      setActiveType((prev) => prev);
      await load(activeType);
      toast('忘掉了', 'success');
    } catch (e) {
      toast(e.message || '没删掉', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    const content = newText.trim();
    if (!content) {
      toast('想让我记住什么？');
      return;
    }
    setSaving(true);
    try {
      await addMemory({ content, type: newType });
      setNewText('');
      setAdding(false);
      setActiveType('');
      await load('');
      toast('记住了', 'success');
    } catch (e) {
      toast(e.message || '没记住', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('确定要我全部忘掉吗？删掉之后就找不回来了。')) {
      return;
    }
    setSaving(true);
    try {
      await clearMemories();
      setActiveType('');
      await load('');
      toast('都忘了', 'success');
    } catch (e) {
      toast(e.message || '没清掉', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data.list.length && totalCount === 0) {
    return <div className="page center muted">正在打开…</div>;
  }

  return (
    <div className="page memories">
      <h1 className="memories-title">AI 记住的我</h1>

      {/* 开关 + 说明 */}
      <div className="card memories-head">
        <div className="memories-head-main">
          <strong>
            {data.enabled === 1 ? `记着 ${totalCount} 条` : '记忆已关闭'}
            <span className="muted"> · 上限 {data.max} 条</span>
          </strong>
          <span className="muted">
            {data.enabled === 1
              ? '这些是分析你的事时，AI 会参考的背景。它只服务于你自己的决策，不会出现在分享页，也不给别人看。'
              : '关掉之后，AI 不再记住任何新东西，也不会拿这些去参考。已经记下的内容还在，你可以继续删。'}
          </span>
          <Link to="/settings" className="memories-head-link">
            去偏好设置
          </Link>
        </div>
        <button
          type="button"
          className={`switch ${data.enabled === 1 ? 'is-on' : ''}`}
          onClick={handleToggleMemory}
          disabled={saving}
          aria-label="记忆开关"
        >
          <span className="switch-knob" />
        </button>
      </div>

      {/* 分类筛选 */}
      <div className="memories-filters">
        {TYPE_META.map((item) => {
          const count = item.value === '' ? totalCount : Number(data.types[item.value] || 0);
          return (
            <button
              key={item.value || 'all'}
              type="button"
              className={`memories-filter ${activeType === item.value ? 'is-active' : ''}`}
              onClick={() => setActiveType(item.value)}
            >
              {item.label}
              {count > 0 && <span className="memories-filter-count">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* 手动添加 */}
      <div className="card memories-add">
        {adding ? (
          <div className="memories-add-form">
            <textarea
              className="memories-textarea"
              rows={2}
              maxLength={120}
              placeholder="比如：我买东西更看重性价比，不太在意牌子"
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
            />
            <div className="memories-add-actions">
              <select
                className="memories-select"
                value={newType}
                onChange={(e) => setNewType(e.target.value)}
              >
                {TYPE_META.filter((t) => t.value !== '').map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <button type="button" className="btn btn-primary btn-sm" onClick={handleAdd} disabled={saving}>
                记住
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setAdding(false);
                  setNewText('');
                }}
                disabled={saving}
              >
                算了
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="memories-add-btn" onClick={() => setAdding(true)}>
            ＋ 想让我记住点什么
          </button>
        )}
      </div>

      {/* 列表 */}
      {data.list.length === 0 ? (
        <div className="card memories-empty">
          <strong>{activeType === '' ? '还什么都没记住' : '这一类还没有'}</strong>
          <span className="muted">
            你做完一次选择、或者回来复盘一次，我就会慢慢了解你的偏好。也可以在上边直接写一句。
          </span>
        </div>
      ) : (
        <div className="memories-list">
          {data.list.map((item) => (
            <div key={item.id} className="card memories-item">
              <div className="memories-item-head">
                <span className="memories-badge">{item.type_label}</span>
                <span className="muted memories-item-source">{SOURCE_LABEL[item.source] || item.source}</span>
                {item.use_count > 0 && (
                  <span className="muted memories-item-used">用过 {item.use_count} 次</span>
                )}
              </div>

              {editingId === item.id ? (
                <div className="memories-edit">
                  <textarea
                    className="memories-textarea"
                    rows={2}
                    maxLength={120}
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                  />
                  <div className="memories-edit-actions">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSaveEdit(item.id)}
                      disabled={saving}
                    >
                      保存
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        setEditingId(0);
                        setEditingText('');
                      }}
                      disabled={saving}
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="memories-item-text">{item.content}</p>
                  <div className="memories-item-foot">
                    <span className="muted">{formatFull(item.created_at)}</span>
                    <div className="memories-item-ops">
                      <button
                        type="button"
                        className="memories-op"
                        onClick={() => {
                          setEditingId(item.id);
                          setEditingText(item.content);
                        }}
                        disabled={saving}
                      >
                        改
                      </button>
                      <button
                        type="button"
                        className="memories-op memories-op-danger"
                        onClick={() => handleDelete(item.id)}
                        disabled={saving}
                      >
                        删
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {totalCount > 0 && (
        <div className="memories-footer">
          <button type="button" className="btn btn-ghost btn-sm" onClick={handleClearAll} disabled={saving}>
            全部忘掉
          </button>
        </div>
      )}
    </div>
  );
}
